import * as Lucide from "lucide-react";
import { ArrowRight } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { PageHero, SectionLabel, GhostNumber, CTA } from "@/components/site/Bits";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/Reveal";
import { Counter } from "@/components/site/Counter";
import { PILLARS } from "@/data/content";

function Icon({ name, className }) {
  const C = Lucide[name] || Lucide.Circle;
  return <C className={className} />;
}

const MATTER_STATS = [
  { value: 100, suffix: "%", label: "Pre-verified placements before day one" },
  { text: "Extremely low", label: "Compliance risk profile vs. unmanaged placements" },
  { value: 2, suffix: "", text: "Cities", label: "Southampton and London covered" },
  { text: "Full", label: "Documentation retained for every placement" },
];

export default function Compliance() {
  useSeo(
    "Compliance — Extremely low compliance risk, professionally managed | UK Student Job",
    "UK Student Job manages Right to Work, BRP verification, CAS review, and work-right briefings for every international candidate placement across Southampton and London. Fully documented, audit-ready."
  );

  return (
    <>
      <PageHero
        testId="compliance-hero"
        label="Our compliance approach"
        lines={[<>Extremely low compliance risk.</>, <span key="r" className="text-[#C8102E]">Professionally managed.</span>, <>Fully documented.</>]}
        sub="International candidate employment carries genuine immigration compliance considerations. UK Student Job manages every layer of the process to keep risk extremely low — for every consultancy partner, every placement, in Southampton and London."
      >
        <CTA to="/contact" testId="compliance-hero-cta">
          Speak to the team <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </CTA>
      </PageHero>

      {/* Four pillars */}
      <section className="bg-[#F4F6F9] py-24 lg:py-32" data-testid="compliance-pillars">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-3xl">
            <Reveal><SectionLabel>Four compliance pillars</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl tracking-tight">
                Our four-layer verification and guidance process
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-[#12294D]/75 leading-relaxed font-manrope">
                Every candidate placed through UK Student Job passes through all four layers before
                their first shift begins. Our structured approach keeps compliance risk extremely low
                — whether the placement is in Southampton or London.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 space-y-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="relative bg-white rounded-2xl p-8 lg:p-10 border border-[#0A1F44]/8 shadow-[0_8px_32px_rgba(10,31,68,0.05)] grid md:grid-cols-12 gap-8 items-start overflow-hidden">
                  <GhostNumber className="absolute -top-4 right-6 text-[9rem] hidden lg:block">{`0${i + 1}`}</GhostNumber>
                  <div className="md:col-span-2">
                    <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0A1F44] text-white">
                      <Icon name={p.icon} className="h-8 w-8" />
                    </div>
                  </div>
                  <div className="md:col-span-10 relative">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-outfit font-bold text-xl lg:text-2xl text-[#0A1F44]">{`0${i + 1} · ${p.title}`}</h3>
                    </div>
                    <p className="mt-3 text-[#12294D]/75 leading-relaxed font-manrope">{p.body}</p>
                    {p.fine && (
                      <p className="mt-4 text-[#12294D]/50 text-xs leading-relaxed font-manrope italic border-l-2 border-[#C8102E]/40 pl-4">
                        {p.fine}
                      </p>
                    )}
                    <div className="mt-5 inline-flex items-center gap-2 bg-[#C8102E]/10 text-[#C8102E] text-xs font-semibold px-4 py-2 rounded-full">
                      <Lucide.BadgeCheck className="h-4 w-4" /> {p.note}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why compliance matters */}
      <section className="relative bg-[#0A1F44] py-24 lg:py-32 overflow-hidden grain" data-testid="compliance-matters">
        <div className="absolute inset-0 flag-stripes opacity-25" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <Reveal><SectionLabel light>Why compliance matters</SectionLabel></Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-5 font-outfit font-extrabold text-white text-3xl lg:text-5xl leading-[1.05] tracking-tight">
                  The risk is real — and it needs to be properly managed
                </h2>
              </Reveal>
              <div className="mt-8 space-y-5 text-white/70 leading-relaxed font-manrope">
                <Reveal delay={0.1}><p>Under UK law, it is the employing organisation's responsibility to ensure that every worker has the legal right to work in the United Kingdom. For international candidates, this means verifying their Right to Work, confirming their BRP, and ensuring their working hours are consistent with their visa conditions.</p></Reveal>
                <Reveal delay={0.14}><p>UK Student Job manages every layer of this compliance assurance on behalf of our consultancy partners. When your consultancy places a candidate through us — in Southampton or London — the compliance verification is handled, documented, and available for audit purposes.</p></Reveal>
                <Reveal delay={0.18}><p>When you place a candidate through UK Student Job, your consultancy benefits from our full compliance infrastructure. We carry out the verification, we document everything, and we provide the guidance framework that keeps risk extremely low at every stage.</p></Reveal>
              </div>
            </div>
            <div className="lg:col-span-5">
              <RevealStagger className="grid sm:grid-cols-2 gap-4">
                {MATTER_STATS.map((s, i) => (
                  <RevealItem key={i}>
                    <div className="h-full bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur-sm">
                      <div className="text-3xl lg:text-4xl font-outfit font-extrabold text-[#C8102E]">
                        {s.value !== undefined ? <Counter to={s.value} suffix={s.suffix} /> : s.text}
                        {s.value !== undefined && s.text ? <span className="block text-white text-xl">{s.text}</span> : null}
                      </div>
                      <p className="mt-3 text-white/70 text-sm leading-relaxed font-manrope">{s.label}</p>
                    </div>
                  </RevealItem>
                ))}
              </RevealStagger>
            </div>
          </div>
        </div>
      </section>

      {/* Page CTA */}
      <section className="bg-white py-24 lg:py-32" data-testid="compliance-cta">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="relative rounded-[2rem] bg-[#F4F6F9] overflow-hidden p-10 lg:p-16 text-center border border-[#0A1F44]/8">
            <div className="absolute top-0 right-0 w-1/2 h-full flag-stripes opacity-60" />
            <div className="relative max-w-3xl mx-auto">
              <Reveal>
                <h2 className="font-outfit font-extrabold text-[#0A1F44] text-3xl lg:text-5xl leading-[1.05] tracking-tight">
                  Compliance confidence from day one
                </h2>
              </Reveal>
              <Reveal delay={0.05}>
                <p className="mt-6 text-[#12294D]/75 leading-relaxed font-manrope">
                  Partner with UK Student Job and benefit from a structured, documented compliance
                  approach for every international candidate placement. We verify, we brief, we
                  document — so your consultancy operates with confidence.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-9 flex flex-wrap gap-4 justify-center">
                  <CTA to="/contact" testId="compliance-cta-primary">
                    Speak to the team <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </CTA>
                  <CTA to="/for-consultancies" variant="secondary" testId="compliance-cta-secondary">
                    Consultancy partnership
                  </CTA>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
